/**
 * ClientNotesTab — Internal AM notes (client cannot see)
 */

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  StickyNote,
  Pin,
  PinOff,
  Trash2,
  Pencil,
  Check,
  X,
  Loader2,
  Lock,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import type { ClientNote } from "@/hooks/useAMClientDetail";

interface Props {
  notes: ClientNote[];
  onAdd: (body: string) => Promise<any>;
  onUpdate: (noteId: string, updates: { body?: string; pinned?: boolean }) => Promise<boolean>;
  onDelete: (noteId: string) => Promise<boolean>;
}

export default function ClientNotesTab({ notes, onAdd, onUpdate, onDelete }: Props) {
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  // Sort: pinned first, then by created_at desc
  const sorted = [...notes].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    const result = await onAdd(newNote.trim());
    setSaving(false);
    if (result) {
      setNewNote("");
      toast.success("Note added");
    } else {
      toast.error("Failed to add note");
    }
  };

  const handleTogglePin = async (note: ClientNote) => {
    const ok = await onUpdate(note.id, { pinned: !note.pinned });
    if (!ok) toast.error("Failed to update note");
  };

  const handleDelete = async (noteId: string) => {
    const ok = await onDelete(noteId);
    if (ok) {
      toast.success("Note deleted");
    } else {
      toast.error("Failed to delete note");
    }
  };

  const handleEditSave = async (noteId: string) => {
    if (!editBody.trim()) return;
    const ok = await onUpdate(noteId, { body: editBody.trim() });
    if (ok) {
      setEditingId(null);
      toast.success("Note updated");
    } else {
      toast.error("Failed to update note");
    }
  };

  return (
    <div className="space-y-6">
      {/* Privacy Notice */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
        <Lock className="h-3.5 w-3.5 flex-shrink-0" />
        Internal notes — only visible to Account Managers. Clients cannot see these notes.
      </div>

      {/* Add Note */}
      <Card>
        <CardContent className="pt-6">
          <Textarea
            placeholder="Add an internal note about this client..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="min-h-[80px] resize-none text-sm"
            rows={3}
          />
          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={handleAdd} disabled={!newNote.trim() || saving}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Add Note
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notes List */}
      {sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <StickyNote className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No notes yet. Add your first note above.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((note) => (
            <Card key={note.id} className={note.pinned ? "border-[#D4A853]/30 bg-[#D4A853]/5" : ""}>
              <CardContent className="pt-4 pb-3">
                {editingId === note.id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      className="min-h-[60px] resize-none text-sm"
                      rows={3}
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        <X className="w-3.5 h-3.5 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={() => handleEditSave(note.id)} disabled={!editBody.trim()}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm whitespace-pre-wrap flex-1">{note.body}</p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleTogglePin(note)}
                          title={note.pinned ? "Unpin" : "Pin"}
                        >
                          {note.pinned ? (
                            <PinOff className="h-3.5 w-3.5 text-[#D4A853]" />
                          ) : (
                            <Pin className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => { setEditingId(note.id); setEditBody(note.body); }}
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleDelete(note.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {note.pinned && (
                        <Badge variant="outline" className="text-[10px] border-[#D4A853]/30 text-[#D4A853]">
                          Pinned
                        </Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {format(new Date(note.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </span>
                      {note.updated_at !== note.created_at && (
                        <span className="text-[10px] text-muted-foreground">(edited)</span>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
